import React from 'react';
import { Link } from 'react-router-dom';
import { ImageWithHoverPreview } from '../../components/CardHoverPreview/CardHoverPreview';
import { formatEastern } from '../../utils/formatDate';
import { priceChartingUrl } from '../../utils/priceCharting';

// Re-usable cell renderers for cert-bearing rows.
// Both the Certs tab (cert directly) and Listings tab (cert nested under listing.cert)
// share the same data shape under `cert`.

export const formatMoney = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return `$${n.toFixed(2)}`;
};

export const gradeNumber = (cert) => {
    if (!cert?.grade) return null;
    const m = cert.grade.match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
};

// Compact date (MM/DD/YY) for the "last priced" line under the grade breakdown.
export const formatPriceDate = (dateString) => formatEastern(dateString, {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
});

// Suggested listing price = PSA-matched PriceCharting price, else generic PriceCharting price.
export const suggestedPrice = (cert) => {
    if (!cert) return '';
    const g = gradeNumber(cert);
    if (g && cert.pricing_data?.[`psa_${g}`]) return cert.pricing_data[`psa_${g}`];
    if (cert.price_charting_price) return cert.price_charting_price;
    return '';
};

// Cert thumbnail with the hover preview. With `listingHref`, the image opens
// that listing in a new tab and a "View Listing" link sits beneath it.
export const CertImage = ({ cert, listingHref }) => {
    const src = cert?.cert_images?.front_image_url;
    const image = src ? (
        <ImageWithHoverPreview
            src={src}
            alt={cert?.name ? `Front: ${cert.name}` : 'Cert front'}
            className="cert-image"
        />
    ) : <span className="cert-image-placeholder" aria-hidden="true">—</span>;

    if (!listingHref) return image;
    return (
        <div className="cert-image-listing">
            <Link to={listingHref} target="_blank" rel="noopener noreferrer" title="View Listing" className="cert-image-link">
                {image}
            </Link>
            <Link to={listingHref} target="_blank" rel="noopener noreferrer" className="broker-link cert-image-listing-link">
                View Listing
            </Link>
        </div>
    );
};

// Cert number linking to the cert on PSA, in a new tab.
export const PsaCertLink = ({ certNumber }) => (
    certNumber ? (
        <a
            href={`https://www.psacard.com/cert/${encodeURIComponent(certNumber)}/psa`}
            target="_blank"
            rel="noopener noreferrer"
            className="broker-link"
            title="View on PSA"
        >
            {certNumber}
        </a>
    ) : '—'
);

export const PriceChartingGradesCell = ({ cert }) => {
    if (!cert?.pricing_data) return <span className="muted">—</span>;
    const myGrade = gradeNumber(cert);
    const updated = formatPriceDate(cert.pricing_data.date_updated);
    return (
        <div className="price-charting-prices">
            {[10, 9, 8, 7].map((g) => {
                const key = `psa_${g}`;
                const price = cert.pricing_data[key];
                const isBold = myGrade === g;
                return (
                    <div key={key} className={isBold ? 'bold-price' : ''}>
                        <span className="grade-label">{g}:</span>{' '}
                        {price ? `$${price}` : 'N/A'}
                    </div>
                );
            })}
            {updated && <div className="price-updated">Updated {updated}</div>}
        </div>
    );
};

export const PriceChartingProductCell = ({ cert }) => {
    if (!cert?.pricing_data?.product_name || !cert?.price_charting_id) {
        return <span className="muted">—</span>;
    }
    return (
        <a
            href={priceChartingUrl(cert)}
            target="_blank"
            rel="noopener noreferrer"
            className="broker-link"
        >
            {cert.pricing_data.product_name}
        </a>
    );
};

export const certMatchesQuery = (cert, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [
        cert?.cert_number,
        cert?.name,
        cert?.card?.set?.name,
        cert?.set_number,
        cert?.grade,
        cert?.pricing_data?.product_name,
    ].some((v) => (v ? String(v).toLowerCase().includes(q) : false));
};
